import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "neosync_session_token";

export const getToken = () => {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
};

export const setToken = (token) => {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* ignore */
  }
};

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Attach Authorization header from localStorage on every request.
// We rely on this (not the httpOnly cookie) because Cloudflare's edge strips
// the credentials flag on preflighted cross-origin requests.
api.interceptors.request.use((config) => {
  const t = getToken();
  if (t) {
    config.headers = config.headers || {};
    if (!config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${t}`;
    }
  }
  return config;
});

// Normalize FastAPI / Pydantic v2 error payloads before they reach any
// callsite. Pydantic returns `detail` as an ARRAY of
// {type, loc, msg, input, url} objects when request-body validation fails.
// Callsites all over the app do `err.response.data.detail || "fallback"`
// and assign the value straight into React state — rendering the array
// as a child triggers React error #31 ("Objects are not valid as a
// React child"). Coerce to a readable string at a single point so no
// callsite has to remember to handle the array shape.
const _stringifyDetail = (detail) => {
  if (detail == null) return detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    // Prefer the `msg` fields joined; fall back to JSON if the shape
    // isn't the classic Pydantic one.
    const msgs = detail
      .map((d) => (d && typeof d === "object" ? d.msg || d.message : null))
      .filter(Boolean);
    if (msgs.length) return msgs.join("; ");
    try {
      return JSON.stringify(detail);
    } catch {
      return "Request failed";
    }
  }
  if (typeof detail === "object") {
    return detail.msg || detail.message || JSON.stringify(detail);
  }
  return String(detail);
};

api.interceptors.response.use(
  (resp) => resp,
  (err) => {
    try {
      const d = err?.response?.data;
      if (d && "detail" in d) {
        d.detail = _stringifyDetail(d.detail);
      }
    } catch {
      /* ignore — best-effort normalization */
    }
    return Promise.reject(err);
  },
);

// The `.mp4` suffix is critical: Cloudflare's default cache rules only
// engage for URLs ending in a recognized media extension. Without it, the
// stream response is marked `cf-cache-status: DYNAMIC` on the edge, so
// every mobile byte-range request round-trips to origin — on cellular
// this manifests as "// BUFFERING" → "STREAM UNAVAILABLE". Adding the
// `.mp4` alias lets CF edge-cache the bytes and makes mobile playback
// smooth. The backend serves the exact same handler on both routes
// (see stream_video in server.py).
export const streamUrl = (videoId) => `${API}/videos/${videoId}/stream.mp4`;
export const posterUrl = (videoId) => `${API}/videos/${videoId}/poster`;
export const photoImageUrl = (videoId) => `${API}/photos/${videoId}/image`;
export const photoAudioUrl = (videoId) => `${API}/photos/${videoId}/audio`;
export const downloadUrl = (videoId) => `${API}/videos/${videoId}/download`;

/** Resolve an avatar/picture string: absolute URLs pass through, relative
 *  backend paths get the backend host prepended. */
export const picUrl = (picture) => {
  if (!picture) return null;
  if (/^https?:\/\//i.test(picture)) return picture;
  if (picture.startsWith("/")) return `${BACKEND_URL}${picture}`;
  return picture;
};
